/* ============================================================================
   VILLAINS TCG — Shared JS Utilities (cst-utils.js)
   Foundation helpers for all cst-* web components. Self-contained ES module
   (no importmap dependency). Also attaches to a frozen `window.CST` namespace
   so future components can use it without deep-importing Horizon internals.
   Ref: MASTER-IMPLEMENTATION-BLUEPRINT.md §13
   ========================================================================== */

/** Breakpoints — mirror cst-tokens.css (mobile-first, Horizon-aligned). */
export const breakpoints = Object.freeze({ tablet: 750, desktop: 990, wide: 1200 });

/** Query one / many elements. */
export const qs = (selector, root = document) => root.querySelector(selector);
export const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

/** Add an event listener and return a disposer. */
export function on(target, type, handler, options) {
  target.addEventListener(type, handler, options);
  return () => target.removeEventListener(type, handler, options);
}

/** Run a handler once. */
export function once(target, type, handler, options) {
  return on(target, type, handler, { ...(options || {}), once: true });
}

/** Debounce — trailing. */
export function debounce(fn, wait = 150) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/** Throttle to one call per animation frame. */
export function rafThrottle(fn) {
  let ticking = false;
  return function throttled(...args) {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      fn.apply(this, args);
    });
  };
}

/** True when the user prefers reduced motion. */
export const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** True on coarse/touch pointers. */
export const isTouch = () =>
  window.matchMedia('(pointer: coarse)').matches ||
  ('ontouchstart' in window && navigator.maxTouchPoints > 0);

/** Min-width media match, e.g. mq('desktop'). */
export const mq = (name) =>
  window.matchMedia(`(min-width: ${breakpoints[name] || name}px)`).matches;

/** Dispatch a cancelable custom event; returns false if prevented. */
export function emit(target, name, detail) {
  return target.dispatchEvent(
    new CustomEvent(name, { detail, bubbles: true, composed: true, cancelable: true })
  );
}

/** Await the next microtask/frame. */
export const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

/** Trap focus within a container; returns a release() function. */
export function trapFocus(container) {
  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function handle(event) {
    if (event.key !== 'Tab') return;
    const nodes = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  return on(container, 'keydown', handle);
}

/** Format a Shopify money value (cents) using the store's format string. */
export function formatMoney(cents, format) {
  const fmt = format || (window.Theme && window.Theme.moneyFormat) || '${{amount}}';
  const amount = (Number(cents) / 100).toFixed(2);
  return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
}

const utils = {
  breakpoints, qs, qsa, on, once, debounce, rafThrottle,
  prefersReducedMotion, isTouch, mq, emit, nextFrame, trapFocus, formatMoney,
};

/* Expose on a shared namespace (single importmap in the theme is Horizon's;
   we share foundation code via CST rather than a competing importmap). */
window.CST = Object.assign(window.CST || {}, { utils });

export default utils;
